import { SkeletonWoo } from '@/components/skeleton/skeleton.woo';
import { useGetPersonalInfo } from '@/hooks/api';
import { WooClose, WooMenu, WooIdWithText } from '@nosinovacao/fe-icons';
import {
  SheetClosePrimitive,
  SheetContentPrimitive,
  SheetPrimitive,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { type ComponentType } from 'react';
import { useNavbarContext, type NavSheetContentSections } from '../../context/navbar';
import { MainNavContentWoo } from './main-section';
import { TvNavSheetContentWoo } from './tv-section';

const navSheetContent: Record<
  NavSheetContentSections,
  ComponentType<{
    onClose: () => void;
    goToSection: (section: Exclude<NavSheetContentSections, 'main'>) => void;
    goToMainSection: () => void;
    currentSection: NavSheetContentSections;
  }>
> = {
  main: MainNavContentWoo,
  tv: TvNavSheetContentWoo,
  services: MainNavContentWoo,
} as const;

export function MobileNavbarWoo() {
  const { t } = useI18n();
  const {
    sheetOpen,
    setSheetOpen,
    activeSection: sectionName,
    setActiveSection: setSectionName,
  } = useNavbarContext();

  const NavSheetContent = navSheetContent[sectionName];

  return (
    <SheetPrimitive open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetContentPrimitive
        skin="light"
        aria-label={t('Common.Navbar.ContentLabel')}
        className="fixed inset-y-0 right-0 z-50 w-dvw bg-nos-neutral-0 py-6 tablet:w-[min(50dvw,384px)] [&>*]:flex [&>*]:flex-col"
      >
        <div className="size-full">
          <div className="flex items-center justify-between gap-5 px-4 pb-6">
            <WooIdWithText className="text-nos-neutral-900" />
            <SheetClosePrimitive aria-label={t('Common.Navbar.Close')} data-tid="button-close-menu">
              <WooClose width={32} height={32} className="text-nos-neutral-900" />
            </SheetClosePrimitive>
          </div>
          <NavSheetContent
            onClose={() => setSheetOpen(false)}
            goToSection={setSectionName}
            goToMainSection={() => setSectionName('main')}
            currentSection={sectionName}
          />
        </div>
      </SheetContentPrimitive>
    </SheetPrimitive>
  );
}

export function MobileNavbarTriggerWoo() {
  const { isLoading } = useGetPersonalInfo();
  const { setSheetOpen } = useNavbarContext();
  return (
    <SkeletonWoo
      loading={isLoading}
      className="bg-transparent"
      loaderChildren={<WooMenu width={32} height={32} />}
    >
      <button
        type="button"
        className="rounded-md text-nos-neutral-1000 outline-none focus-visible:outline-woo-blue"
        data-tid="button-open-menu"
        onClick={() => {
          setSheetOpen(true);
        }}
      >
        <WooMenu width={32} height={32} />
      </button>
    </SkeletonWoo>
  );
}
